import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "./queryKeys";
import type { MapsCallSource } from "~/lib/maps/attribution";
import { geocodeAddress } from "~/lib/server-functions/get-geocoding";

export type GeocodedAddress = {
  lat: number;
  lng: number;
  formattedAddress: string | null;
  viewport?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  };
  bounds?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  };
} | null;

export const buildGeocodedAddressQueryOptions = (
  query: string,
  options: { source: MapsCallSource; enabled?: boolean },
) => ({
  queryKey: [QUERY_KEYS.GEOCODED_ADDRESS, query],
  queryFn: async (): Promise<GeocodedAddress> => {
    if (query.length === 0) return null;
    const result = await geocodeAddress({
      data: { query, source: options.source },
    });
    return result || null;
  },
  enabled: query.length > 3 && (options.enabled ?? true),
  staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  // A failed Geocoding request is still a billed request; never auto-retry it.
  retry: false as const,
});

export const useGetGeocodedAddress = (
  query: string,
  options: { source: MapsCallSource; enabled?: boolean },
) => {
  return useQuery<GeocodedAddress>(
    buildGeocodedAddressQueryOptions(query, options),
  );
};
